import { useMutation, useQuery } from '@tanstack/react-query';
import { customFetcher } from '../useFetch';

export type ExportFormatKey =
  | 'dino'
  | 'strukturdatensatz'
  | 'xjustiz'
  | 'nebenakte';

export interface IAreaOfLawOption {
  value: string;
  label: string;
}

export interface IExportFormatOption {
  key: ExportFormatKey;
  label: string;
  available: boolean;
  reason?: string;
  /** Backend-Hinweis: konkrete XJustiz-Nachrichtenart (z. B. "nachricht.reg.0400003"),
   *  damit das UI dem Notar transparent zeigen kann, was tatsaechlich erzeugt wird. */
  messageType?: string;
  /** Backend-Warnung, die das UI vor dem Export hervorgehoben anzeigt. */
  warning?: string;
  /** Verfuegbare Schema-Versionen (nur fuer XJustiz relevant). */
  versions?: string[];
  /** Default-Version, die das UI vorauswaehlt. */
  defaultVersion?: string;
  /** Rechtsgebiete fuer SDS-Export (areaOfLaw-Dropdown). */
  areaOfLawOptions?: IAreaOfLawOption[];
}

export interface IExportResponse {
  filename: string;
  contentType: string;
  contentBase64: string;
  meta?: Record<string, unknown>;
}

export const useLegalTransactionExportFormats = () => {
  const { data, isLoading } = useQuery<{ formats: IExportFormatOption[] }>({
    refetchOnWindowFocus: false,
    queryKey: ['exportFormats', 'legalTransaction'],
    queryFn: () =>
      customFetcher({ endpoint: '/export/legaltransaction/formats' }),
  });
  return { formats: data?.formats ?? [], isLoading };
};

export const useLegalClientExportFormats = () => {
  const { data, isLoading } = useQuery<{ formats: IExportFormatOption[] }>({
    refetchOnWindowFocus: false,
    queryKey: ['exportFormats', 'legalClient'],
    queryFn: () => customFetcher({ endpoint: '/export/legalclient/formats' }),
  });
  return { formats: data?.formats ?? [], isLoading };
};

export const useExportLegalTransaction = () => {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: ({
      idLegalTransaction,
      format,
      version,
      areaOfLaw,
      includeDocuments,
    }: {
      idLegalTransaction: number;
      format: ExportFormatKey;
      /** Nur fuer XJustiz: konkrete Schema-Version (z. B. "3.6.2"). */
      version?: string;
      /** Nur fuer SDS: Rechtsgebiet (Enum oder freier String). */
      areaOfLaw?: string;
      /** Wenn true und das Format unterstuetzt es (SDS, XJustiz):
       *  Output ist ein ZIP mit Vorgangs-Dokumenten als Anhaengen. */
      includeDocuments?: boolean;
    }) => {
      const body: Record<string, unknown> = { format };
      if (version) body.version = version;
      if (areaOfLaw) body.areaOfLaw = areaOfLaw;
      if (includeDocuments) body.includeDocuments = true;
      return customFetcher({
        endpoint: `/export/legaltransaction/${idLegalTransaction}`,
        method: 'POST',
        body,
      }) as Promise<IExportResponse>;
    },
  });
  return { exportLegalTransaction: mutateAsync, isExporting: isPending };
};

export const useExportLegalClient = () => {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: ({
      idLegalClient,
      format,
      version,
      areaOfLaw,
    }: {
      idLegalClient: number;
      format: ExportFormatKey;
      version?: string;
      areaOfLaw?: string;
    }) => {
      const body: Record<string, unknown> = { format };
      if (version) body.version = version;
      if (areaOfLaw) body.areaOfLaw = areaOfLaw;
      return customFetcher({
        endpoint: `/export/legalclient/${idLegalClient}`,
        method: 'POST',
        body,
      }) as Promise<IExportResponse>;
    },
  });
  return { exportLegalClient: mutateAsync, isExporting: isPending };
};

export const downloadBase64 = (response: IExportResponse) => {
  const linkSource = `data:${response.contentType};base64,${response.contentBase64}`;
  const downloadLink = document.createElement('a');
  downloadLink.href = linkSource;
  downloadLink.download = response.filename;
  downloadLink.click();
};
